import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Link, Divider, IconButton, CircularProgress } from '@mui/material';
import type { Crypto } from '../../types/networkStatus';
import { getExchangeNetworkStatusUrl } from '../../utils/networkCompatibility';
import { getExchangeName } from '../../types/opportunity';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { refreshNetworkStatus } from '../../features/networkStatus/networkStatusSlice';
import { NETWORK_STATUS_COLORS } from '../../constants/colors';

interface NetworkStatusTooltipProps {
  symbol: string;
  buyExchange: number;
  sellExchange: number;
  buyCrypto: Crypto | null;
  sellCrypto: Crypto | null;
}

export const NetworkStatusTooltip: React.FC<NetworkStatusTooltipProps> = ({
  symbol,
  buyExchange,
  sellExchange,
  buyCrypto,
  sellCrypto,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const buyExchangeName = getExchangeName(buyExchange);
  const sellExchangeName = getExchangeName(sellExchange);
  
  // Get loading states for each exchange
  const buyLoading = useAppSelector(state => state.networkStatus?.loading?.[buyExchange] || false);
  const sellLoading = useAppSelector(state => state.networkStatus?.loading?.[sellExchange] || false);
  
  // Refresh handlers
  const handleRefreshBuyExchange = useCallback(() => {
    dispatch(refreshNetworkStatus(buyExchange));
  }, [dispatch, buyExchange]);
  
  const handleRefreshSellExchange = useCallback(() => {
    dispatch(refreshNetworkStatus(sellExchange));
  }, [dispatch, sellExchange]);

  return (
    <Box sx={{ p: 1.5, minWidth: 300 }}>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        {t('networkTooltip.title', { symbol })}
      </Typography>
      
      <Divider sx={{ my: 1 }} />
      
      {/* Buy Exchange Networks */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="primary" fontWeight="bold">
            {t('networkTooltip.buyWithdraw', { exchange: buyExchangeName })}
          </Typography>
          <IconButton 
            size="small" 
            onClick={handleRefreshBuyExchange}
            disabled={buyLoading}
            sx={{ padding: '2px' }}
            title={t('networkTooltip.refreshNetwork')}
          >
            {buyLoading ? (
              <CircularProgress size={16} />
            ) : (
              <span style={{ fontSize: '16px' }}>🔄</span>
            )}
          </IconButton>
        </Box>
        {buyCrypto ? (
          <Box sx={{ mt: 0.5 }}>
            {buyCrypto.networks.length > 0 ? (
              buyCrypto.networks.map((network, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                  <Typography variant="caption" sx={{ flex: 1 }}>
                    {network.network}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: network.withdraw_enabled ? NETWORK_STATUS_COLORS.ENABLED : NETWORK_STATUS_COLORS.DISABLED,
                        fontWeight: 'bold'
                      }}
                    >
                      {t('networkTooltip.withdraw')}: {network.withdraw_enabled ? '✓' : '✗'}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: network.deposit_enabled ? NETWORK_STATUS_COLORS.ENABLED : NETWORK_STATUS_COLORS.DISABLED,
                        fontWeight: 'bold'
                      }}
                    >
                      {t('networkTooltip.deposit')}: {network.deposit_enabled ? '✓' : '✗'}
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('networkTooltip.noNetworkInfo')}
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('networkTooltip.networkDataNotLoaded')}
            </Typography>
            <Link 
              href={getExchangeNetworkStatusUrl(buyExchange)} 
              target="_blank" 
              rel="noopener"
              sx={{ display: 'block', mt: 0.5 }}
            >
              <Typography variant="caption">
                {t('networkTooltip.checkManually')}
              </Typography>
            </Link>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Sell Exchange Networks */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="primary" fontWeight="bold">
            {t('networkTooltip.sellDeposit', { exchange: sellExchangeName })}
          </Typography>
          <IconButton 
            size="small" 
            onClick={handleRefreshSellExchange}
            disabled={sellLoading}
            sx={{ padding: '2px' }}
            title={t('networkTooltip.refreshNetwork')}
          >
            {sellLoading ? (
              <CircularProgress size={16} />
            ) : (
              <span style={{ fontSize: '16px' }}>🔄</span>
            )}
          </IconButton>
        </Box>
        {sellCrypto ? (
          <Box sx={{ mt: 0.5 }}>
            {sellCrypto.networks.length > 0 ? (
              sellCrypto.networks.map((network, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                  <Typography variant="caption" sx={{ flex: 1 }}>
                    {network.network}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: network.withdraw_enabled ? NETWORK_STATUS_COLORS.ENABLED : NETWORK_STATUS_COLORS.DISABLED,
                        fontWeight: 'bold'
                      }}
                    >
                      {t('networkTooltip.withdraw')}: {network.withdraw_enabled ? '✓' : '✗'}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: network.deposit_enabled ? NETWORK_STATUS_COLORS.ENABLED : NETWORK_STATUS_COLORS.DISABLED,
                        fontWeight: 'bold'
                      }}
                    >
                      {t('networkTooltip.deposit')}: {network.deposit_enabled ? '✓' : '✗'}
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('networkTooltip.noNetworkInfo')}
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('networkTooltip.networkDataNotLoaded')}
            </Typography>
            <Link 
              href={getExchangeNetworkStatusUrl(sellExchange)} 
              target="_blank" 
              rel="noopener"
              sx={{ display: 'block', mt: 0.5 }}
            >
              <Typography variant="caption">
                {t('networkTooltip.checkManually')}
              </Typography>
            </Link>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 1 }} />
      
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {t('networkTooltip.withdrawLegend')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {t('networkTooltip.statusLegend')}
      </Typography>
    </Box>
  );
};